"use client";

import { useEffect, useState } from "react";

/**
 * STORY-1302 — minimal display-locale resolver for BETA labeling
 * strings. Closed-beta scope: pt vs en only, no full i18n stack.
 *
 * Resolution order:
 *   1. localStorage `granbay.locale` (set by /settings/account when
 *      user picks one — STORY-103 wired the pref already)
 *   2. navigator.language family — pt-BR / pt-PT / pt-* → "pt"
 *   3. fallback "en"
 *
 * SSR returns "en" deterministically (no window). The hook re-resolves
 * on mount so the first client render rehydrates with the actual
 * preference. The `null` sentinel avoids a hydration mismatch — we
 * keep returning "en" until the effect resolves.
 */
export type DisplayLocale = "pt" | "en";

function resolveLocale(): DisplayLocale {
  try {
    const stored = window.localStorage.getItem("granbay.locale");
    if (stored === "pt" || stored === "en") return stored;
  } catch {
    // localStorage blocked (private mode) — fall through
  }
  const navLang = (navigator.language || "en").toLowerCase();
  return navLang.startsWith("pt") ? "pt" : "en";
}

export function useDisplayLocale(): DisplayLocale {
  const [locale, setLocale] = useState<DisplayLocale | null>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocale(resolveLocale());
  }, []);

  // Pre-mount: deterministic "en" default that matches SSR. After the
  // effect runs, swap to the user's resolved locale.
  return locale ?? "en";
}
